import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Save, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE as API } from '../../config/api';

const STRESS_SYSTEMS = [
  { value: 'fixed_initial', label: 'Fixed Initial' },
  { value: 'fixed_penultimate', label: 'Fixed Penultimate' },
  { value: 'fixed_final', label: 'Fixed Final' },
  { value: 'lexical', label: 'Lexical (Unpredictable)' },
  { value: 'weight_sensitive', label: 'Weight-Sensitive' },
  { value: 'none', label: 'No Stress System' },
];

const TONE_SYSTEMS = [
  { value: 'none', label: 'No Tones' },
  { value: 'register', label: 'Register (Level Tones)' },
  { value: 'contour', label: 'Contour Tones' },
  { value: 'mixed', label: 'Mixed System' },
];

const ProsodyPage = () => {
  const { conlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [stressSystem, setStressSystem] = useState('none');
  const [stressDescription, setStressDescription] = useState('');
  const [toneSystem, setToneSystem] = useState('none');
  const [toneDescription, setToneDescription] = useState('');
  const [intonationPatterns, setIntonationPatterns] = useState('');

  useEffect(() => { fetchData(); }, [conlangId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/conlangs/${conlangId}/prosody`, { withCredentials: true });
      if (res.data) {
        setStressSystem(res.data.stress_system || 'none');
        setStressDescription(res.data.stress_description || '');
        setToneSystem(res.data.tone_system || 'none');
        setToneDescription(res.data.tone_description || '');
        setIntonationPatterns(res.data.intonation_patterns || '');
      }
    } catch { toast.error('Failed to load prosody data'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/conlangs/${conlangId}/prosody`, {
        stress_system: stressSystem,
        stress_description: stressDescription || null,
        tone_system: toneSystem,
        tone_description: toneDescription || null,
        intonation_patterns: intonationPatterns || null,
      }, { withCredentials: true });
      toast.success('Prosody saved');
    } catch { toast.error('Failed to save prosody'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="prosody-title">Prosody</h2>
          <p className="text-foreground-muted mt-1">Define stress, tone, and intonation patterns for {conlang?.name}</p>
        </div>
        {canEdit && (
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-prosody-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
        )}
      </div>

      {/* Stress */}
      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Stress System</CardTitle>
          <CardDescription>How is word stress assigned?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={stressSystem} onValueChange={setStressSystem}>
            <SelectTrigger className="bg-background border-border max-w-xs" data-testid="stress-system-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background-paper border-border">
              {STRESS_SYSTEMS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea
            value={stressDescription}
            onChange={(e) => setStressDescription(e.target.value)}
            placeholder="Describe how stress works in your language. Are there any exceptions? Secondary stress rules? How does stress interact with morphology?"
            className="bg-background border-border min-h-[120px]"
            data-testid="stress-description"
          />
        </CardContent>
      </Card>

      {/* Tone */}
      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Tone System</CardTitle>
          <CardDescription>Does your language use lexical tone?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={toneSystem} onValueChange={setToneSystem}>
            <SelectTrigger className="bg-background border-border max-w-xs" data-testid="tone-system-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background-paper border-border">
              {TONE_SYSTEMS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea
            value={toneDescription}
            onChange={(e) => setToneDescription(e.target.value)}
            placeholder="Describe the tone inventory, tone rules, tone sandhi, and how tones are marked in writing..."
            className="bg-background border-border min-h-[120px]"
            data-testid="tone-description"
          />
        </CardContent>
      </Card>

      {/* Intonation */}
      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Intonation Patterns</CardTitle>
          <CardDescription>Sentence-level pitch patterns</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={intonationPatterns}
            onChange={(e) => setIntonationPatterns(e.target.value)}
            placeholder="Describe intonation contours for statements, questions, commands, lists, etc."
            className="bg-background border-border min-h-[120px]"
            data-testid="intonation-patterns"
          />
        </CardContent>
      </Card>

      <div className="p-4 rounded-md bg-secondary/10 border border-secondary/30 flex gap-3">
        <Info className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
        <div className="text-secondary text-sm">
          <strong>Prosody</strong> covers suprasegmental features: stress (which syllable is prominent), tone (pitch distinctions that change word meaning), and intonation (sentence-level melody).
        </div>
      </div>
    </div>
  );
};

export default ProsodyPage;
